import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, 
  MapPin, 
  Zap, 
  Battery, 
  Cpu, 
  CheckCircle2, 
  Loader2,
  Calendar
} from 'lucide-react';

const ProjectDetailPage = () => {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProject = async () => {
      try {
        const res = await fetch(`https://api.dev.auxo.dk/public/by-slug/da/${slug}`);
        if (!res.ok) {
          if (res.status === 404) throw new Error('Projektet blev ikke fundet.');
          throw new Error('Kunne ikke hente projektet.');
        }
        const data = await res.json();
        setProject(data);
      } catch (err) {
        console.error('Error fetching project:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProject();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen pt-32 pb-24 flex flex-col items-center justify-center bg-slate-50">
        <Loader2 className="w-12 h-12 text-[#62b2c9] animate-spin mb-4" />
        <p className="text-slate-500 font-bold uppercase tracking-wider text-xs">Henter projekt...</p>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="min-h-screen pt-32 pb-24 flex flex-col items-center justify-center bg-slate-50 px-4">
        <div className="bg-white p-8 md:p-12 shadow-xl border border-slate-100 max-w-lg w-full text-center">
          <h2 className="text-2xl font-black text-slate-900 uppercase tracking-tighter mb-4">Fejl</h2>
          <p className="text-slate-600 mb-8">{error || 'Projektet blev ikke fundet.'}</p>
          <Link 
            to="/projekter" 
            className="inline-flex items-center gap-2 bg-slate-900 hover:bg-black text-white px-6 py-3 font-black uppercase tracking-widest text-xs transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Tilbage til projekter
          </Link>
        </div>
      </div>
    );
  }

  const isOperating = project.terms?.['project-status']?.includes('i-drift');
  const mediaUrl = project.video?.[0] || project.image?.[0];
  const isVideo = mediaUrl?.match(/\.(mp4|webm|ogg)$/i);

  return (
    <div className="pt-20 bg-white">
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-end pb-20 bg-slate-950 text-white overflow-hidden">
        <div className="absolute inset-0 z-0">
          {isVideo ? (
            <video 
              src={mediaUrl} 
              autoPlay 
              loop 
              muted 
              playsInline
              className="w-full h-full object-cover opacity-50"
            />
          ) : mediaUrl ? (
            <img 
              src={mediaUrl} 
              alt={project.title?.da || project.title} 
              className="w-full h-full object-cover opacity-50"
            />
          ) : (
            <div className="w-full h-full bg-slate-900"></div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/60 to-transparent"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <Link 
            to="/projekter" 
            className="inline-flex items-center gap-2 text-white/70 hover:text-white text-xs font-black uppercase tracking-widest mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Tilbage til projekter
          </Link>
          
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <div className="flex flex-wrap items-center gap-4 mb-6">
              <span className={`px-3 py-1 text-[10px] font-black uppercase tracking-widest border ${
                isOperating
                  ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30'
                  : 'bg-[#62b2c9]/20 text-[#62b2c9] border-[#62b2c9]/30'
              }`}>
                {isOperating ? 'I drift' : 'Under opførelse'}
              </span>
              <span className="text-sm font-bold text-slate-300 flex items-center gap-1.5">
                <MapPin className="w-4 h-4" /> {project.location?.da || project.location}
              </span>
            </div>
            
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-black mb-6 leading-tight uppercase tracking-tighter">
              {project.title?.da || project.title}
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-24">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-12 gap-16 items-start">
            
            {/* Left Column: Main Content */}
            <div className="lg:col-span-7 lg:col-start-2">
              <div className="prose prose-lg prose-slate max-w-none">
                <h2 className="text-3xl font-black text-slate-900 uppercase tracking-tighter mb-8">
                  Om projektet
                </h2>
                <div 
                  className="text-slate-600 leading-relaxed mb-12 font-medium"
                  dangerouslySetInnerHTML={{ __html: project.description?.da || project.description }}
                />

                {project.customer_satisfaction && (
                  <>
                    <h3 className="text-2xl font-black text-slate-900 uppercase tracking-tighter mb-6 mt-16 flex items-center gap-3">
                      <CheckCircle2 className="w-8 h-8 text-[#62b2c9]" />
                      Dokumenteret driftssikkerhed
                    </h3>
                    <div className="bg-slate-50 p-8 md:p-10 border-l-4 border-[#62b2c9] shadow-sm">
                      <div 
                        className="text-slate-700 leading-relaxed font-medium italic"
                        dangerouslySetInnerHTML={{ __html: project.customer_satisfaction?.da || project.customer_satisfaction }}
                      />
                    </div>
                  </>
                )}

                {project.image && project.image.length > 0 && (
                  <div className="mt-16">
                    <h3 className="text-2xl font-black text-slate-900 uppercase tracking-tighter mb-8">
                      Billeder fra anlægget
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {project.image.map((imgUrl, idx) => {
                        const optimizedUrl = imgUrl.replace('assets.auxodev.dk/assets', 'assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets');
                        return (
                          <div key={idx} className="aspect-video bg-slate-100 overflow-hidden">
                            <img 
                              src={optimizedUrl} 
                              alt={`Billede ${idx + 1} fra ${project.title?.da || project.title}`} 
                              className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Right Column: Facts Sidebar */}
            <div className="lg:col-span-3">
              <div className="bg-slate-50 border border-slate-100 p-8 sticky top-28">
                <h3 className="text-sm font-black text-slate-900 uppercase tracking-widest mb-6 pb-4 border-b border-slate-200">
                  Fakta om anlægget
                </h3>
                
                <div className="space-y-6">
                  <div>
                    <div className="flex items-center gap-2 text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">
                      <Battery className="w-3.5 h-3.5" /> Kapacitet
                    </div>
                    <div className="text-xl font-black text-slate-900">{project.capacity}</div>
                  </div>
                  
                  <div>
                    <div className="flex items-center gap-2 text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">
                      <Zap className="w-3.5 h-3.5" /> Effekt
                    </div>
                    <div className="text-xl font-black text-slate-900">{project.power}</div>
                  </div>

                  {project.pcs && (
                    <div>
                      <div className="flex items-center gap-2 text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">
                        <Cpu className="w-3.5 h-3.5" /> PCS
                      </div>
                      <div className="text-lg font-bold text-slate-700">{project.pcs}</div>
                    </div>
                  )}

                  {project.battery_type && (
                    <div>
                      <div className="flex items-center gap-2 text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">
                        <Battery className="w-3.5 h-3.5" /> Batterier
                      </div>
                      <div className="text-lg font-bold text-slate-700">{project.battery_type}</div>
                    </div>
                  )}
                  
                  {isOperating && (
                    <div className="pt-6 mt-6 border-t border-slate-200">
                      <div className="flex items-center gap-3 text-emerald-600">
                        <CheckCircle2 className="w-5 h-5" />
                        <span className="text-sm font-bold uppercase tracking-wider">I stabil drift</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
};

export default ProjectDetailPage;